import { useEffect, useState } from 'react';
import firebase from 'firebase/app';

/**
 * The customer's main tags (Aaron, 5 Nov 2026): the 8–10 categories set in Admin › Settings, e.g.
 * film, commercial, corporate. They're the only tags in the home sort/filter menu, and the quick
 * "+ tag" chips on a project. People can still type other tags on a project; those aren't listed.
 * Stored at settings/tags { main: string[] } (anyone with a role can read; admins edit).
 */
export const DEFAULT_MAIN_TAGS = ['film', 'commercial', 'corporate', 'social', 'podcast', 'animation', 'audio', 'digital', 'music', 'video'];
export const MAX_MAIN_TAGS = 12;

let cache: string[] | null = null;
const listeners = new Set<(t: string[]) => void>();
let started = false;

export const cleanTags = (list: unknown): string[] => {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const v of list) {
    const t = String(v || '').trim().toLowerCase();
    if (t && t.length <= 40 && out.indexOf(t) === -1) out.push(t);
  }
  return out.slice(0, MAX_MAIN_TAGS);
};

function start() {
  if (started) return;
  started = true;
  firebase.auth().onAuthStateChanged((user) => {
    if (!user) return;
    firebase
      .firestore()
      .collection('settings')
      .doc('tags')
      .onSnapshot(
        (d) => {
          const saved = cleanTags(d.get('main'));
          cache = saved.length ? saved : DEFAULT_MAIN_TAGS;
          listeners.forEach((fn) => fn(cache!));
        },
        () => undefined,
      );
  });
}

const useMainTags = (): string[] => {
  const [tags, setTags] = useState<string[]>(cache || DEFAULT_MAIN_TAGS);
  useEffect(() => {
    start();
    listeners.add(setTags);
    if (cache) setTags(cache);
    return () => {
      listeners.delete(setTags);
    };
  }, []);
  return tags;
};

export default useMainTags;
